import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowRight } from 'lucide-react';
import { auth, ApiError, RateLimitError } from '../lib/apiClient';
import { AuthCard } from '../components/auth/AuthCard';
import { ErrorBanner } from '../components/auth/ErrorBanner';
import { RateLimitInline, isRateLimited } from '../components/RateLimitBanner';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [rateLimitedUntil, setRateLimitedUntil] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || isRateLimited(rateLimitedUntil)) return;
    setError(null);
    setSubmitting(true);
    try {
      await auth.forgotPassword(email.trim());
      // Backend returns 204 unconditionally to avoid email enumeration — show the same message either way.
      setSubmitted(true);
    } catch (err) {
      if (err instanceof RateLimitError) {
        setRateLimitedUntil(Date.now() + err.retryAfterSeconds * 1000);
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Could not send the reset email. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <AuthCard
        publicTheme
        eyebrow="Reset link sent"
        title="Check your inbox"
        subtitle={`If an account exists for ${email.trim()}, a link to reset or set your password is on its way. Check your inbox and spam folder.`}
      >
        <Link
          to="/signin"
          className="block w-full py-4 bg-brand-primary text-white rounded-2xl font-black text-sm uppercase tracking-widest text-center hover:bg-brand-deep transition-all"
        >
          Back to sign in
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      publicTheme
      eyebrow="Forgot password"
      title="Reset your password"
      subtitle="Enter the email you used to sign up. We&apos;ll send you a link to set a new password."
      footer={
        <>
          Remembered it?{' '}
          <Link to="/signin" className="text-brand-primary font-bold hover:text-brand-accent transition-colors">
            Sign in
          </Link>
        </>
      }
    >
      <ErrorBanner message={error} />
      <RateLimitInline deadlineMs={rateLimitedUntil} className="text-sm text-amber-900 font-medium mb-3" />

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div className="relative">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          <input
            type="email"
            required
            placeholder="Email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-2 focus:ring-brand-accent focus:border-transparent outline-none transition-all font-medium"
          />
        </div>

        <button
          type="submit"
          disabled={submitting || !email.trim() || isRateLimited(rateLimitedUntil)}
          className="w-full py-4 bg-brand-primary text-white rounded-2xl font-black text-sm uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-brand-deep transition-all shadow-lg shadow-brand-primary/10 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Sending…' : 'Send reset link'}
          {!submitting && <ArrowRight className="w-4 h-4" />}
        </button>
      </form>
    </AuthCard>
  );
}
